class ScrollUtil {
  /**
   * Creates a new ScrollUtil instance
   * @param {Object} options - Configuration options
   * @param {Function} options.scrollUpFunction - Function to execute when scrolling up
   * @param {Function} options.scrollDownFunction - Function to execute when scrolling down
   * @param {number} options.scrollDownThreshold - Minimum scrollY position to trigger scroll down callback (default: 0)
   * @param {number} options.throttleMs - Throttle delay in milliseconds (default: 16)
   * @param {number} options.disableDuration - Duration to disable scroll callbacks after execution (default: 150)
   * @throws {TypeError} When parameters are not functions
   */
  constructor(options = {}) {
    // Validate that both parameters are functions
    if (typeof options.scrollUpFunction !== 'function') {
      throw new TypeError('scrollUpFunction must be a function');
    }
    if (typeof options.scrollDownFunction !== 'function') {
      throw new TypeError('scrollDownFunction must be a function');
    }

    this.scrollUpFunction = options.scrollUpFunction;
    this.scrollDownFunction = options.scrollDownFunction;
    this.scrollDownThreshold = options.scrollDownThreshold || 0;
    this.throttleMs = options.throttleMs || 16; // ~60fps
    this.disableDuration = options.disableDuration || 300; // Duration to disable after callback execution

    this.state = {
      current: 'idle',
      lastDirection: null,
      scrollY: window.scrollY,
      lastScrollY: window.scrollY,
    };

    this.lastThrottleTime = 0;
    this.isProgrammaticScroll = false; // Flag for programmatic scroll
    this.disableTimeout = null; // Store timeout reference for cancellation

    // Bind the method to preserve 'this' context
    this.boundScrollEvent = this.scrollEvent.bind(this);

    // Add event listener to execute on scroll
    document.addEventListener('scroll', this.boundScrollEvent, {
      passive: true,
    });
  }

  scrollEvent() {
    const now = Date.now();

    // Throttle the scroll event
    if (now - this.lastThrottleTime < this.throttleMs) {
      return;
    }
    this.lastThrottleTime = now;

    const currentScrollY = window.scrollY;

    // Skip if programmatic scroll is active
    if (this.isProgrammaticScroll) {
      // Still update scroll position
      this.state.scrollY = currentScrollY;
      return;
    }

    const scrollDifference = currentScrollY - this.state.scrollY;

    // Determine scroll direction first
    const isScrollingDown = scrollDifference > 0;
    const isScrollingUp = scrollDifference < 0;

    // Check if direction changed - if so, cancel any pending disable timeout
    const currentDirection = isScrollingDown
      ? 'down'
      : isScrollingUp
        ? 'up'
        : null;
    if (
      currentDirection &&
      currentDirection !== this.state.lastDirection &&
      this.isProgrammaticScroll
    ) {
      this.cancelDisable();
    }

    // Apply scrollDownThreshold: only trigger scroll down if scrollY >= threshold
    if (isScrollingDown && currentScrollY < this.scrollDownThreshold) {
      return;
    }

    // For scroll up, proceed with any movement (no threshold)
    if (!isScrollingDown && !isScrollingUp) {
      return; // No movement
    }

    // Update state
    this.state.lastScrollY = this.state.scrollY;
    this.state.scrollY = currentScrollY;

    // Execute appropriate callback based on direction
    if (isScrollingDown) {
      // Scrolling down (with threshold applied)
      if (this.state.lastDirection !== 'down') {
        this.state.current = 'scrolling-down';
        this.state.lastDirection = 'down';
        this.scrollDownFunction(this.getState());
        // Auto-disable temporarily after callback execution
        this.disableTemporarily(this.disableDuration);
      }
    } else if (isScrollingUp) {
      // Scrolling up (automatic, no threshold)
      if (this.state.lastDirection !== 'up') {
        this.state.current = 'scrolling-up';
        this.state.lastDirection = 'up';
        this.scrollUpFunction(this.getState());
        // Auto-disable temporarily after callback execution
        this.disableTemporarily(this.disableDuration);
      }
    }
  }

  /**
   * Cancel the current disable timeout and re-enable scroll callbacks
   */
  cancelDisable() {
    if (this.disableTimeout) {
      clearTimeout(this.disableTimeout);
      this.disableTimeout = null;
    }
    this.isProgrammaticScroll = false;
  }

  /**
   * Temporarily disable scroll callbacks (for programmatic scrolls)
   * @param {number} duration - Duration to disable in milliseconds (uses configured disableDuration if not provided)
   */
  disableTemporarily(duration) {
    const disableDuration = duration || this.disableDuration;

    // Cancel any existing timeout
    this.cancelDisable();

    // Set new disable state
    this.isProgrammaticScroll = true;
    this.disableTimeout = setTimeout(() => {
      this.isProgrammaticScroll = false;
      this.disableTimeout = null;
    }, disableDuration);
  }

  /**
   * Manually trigger scroll event (useful for testing or forced updates)
   */
  trigger() {
    this.scrollEvent();
  }

  /**
   * Get current scroll state
   * @returns {Object} Current state object
   */
  getState() {
    return { ...this.state };
  }

  /**
   * Destroy the ScrollUtil instance and clean up event listeners
   */
  destroy() {
    if (this.boundScrollEvent) {
      document.removeEventListener('scroll', this.boundScrollEvent);
      this.boundScrollEvent = null;
    }

    // Clean up any pending timeout
    this.cancelDisable();
  }
}

export default ScrollUtil;
